/**
 * Vue Store related to current user preferences.
 *
 * @author Documill 2024
 */

import apiCall from '../../utils/api';
import {logger} from '../../utils/logger';

export default {

  // Note: Same structure as com.documill.platform.flow.controller.api.model.UserPreferencesApiResponse
  //       with the exceptions that the 'isLoaded'-flag is there and the root level properties are
  //       in their own 'userPreferences'-object.

  state: {

    // -- General --
    /** Whether the state has been fetched from the back-end. */
    isLoaded: false,

    // -- User preferences --
    userPreferences: {
      emailNotifications: false,
      // See com.documill.platform.flow.controller.entity.EmailNotificationsReason
      emailNotificationsReason: null, // String
      slackIntegration: false,
      salesforceIntegration: false,
      salesforceChatter: false,
      salesforceGroupId: null, // String
      msTeamsIntegration: false
    },

    // -- General User Notification preferences --
    userNotificationPreferences: {
      enabled: false,
      dailyOverdueTaskSummary: false,
      rejectionResolved: false,
      taskInProgress: false,
      teamLeaderInvitation: false,
      weeklyTaskSummary: false
    },

    // -- Project Owner Notification preferences --
    userPONotificationPreferences: {
      enabled: false,
      fileRejected: false,
      projectCompleted: false,
      projectOverdue: false,
      projectOverdueSoon: false,
      statusChange: false,
      unassignedFromOwnerRole: false,
      taskInProgressPo: false
    }

  },

  getters: {
    // -- General --
    isUserPreferencesLoaded: state => state.isLoaded,

    // -- User preferences --
    isEmailNotificationsEnabled: state => state.userPreferences.emailNotifications,
    getEmailNotificationsReason: state => state.userPreferences.emailNotificationsReason || '',
    isSlackIntegrationEnabled: state => state.userPreferences.slackIntegration,
    isSalesforceIntegrationEnabled: state => state.userPreferences.salesforceIntegration,
    isSalesforceChatterEnabled: state => state.userPreferences.salesforceChatter,
    getSalesforceGroupId: state => state.userPreferences.salesforceGroupId || '',
    isMSTeamsIntegrationEnabled: state => state.userPreferences.msTeamsIntegration,

    // -- General User Notification preferences --
    // TODO: Getters for the fields
    isUserNotificationEnabled: state => state.userNotificationPreferences.enabled,
    getUserNotificationPreferences: state => state.userNotificationPreferences,

    // -- Project Owner Notification preferences --
    // TODO: Getters for the fields
    isUserPONotificationEnabled: state => state.userPONotificationPreferences.enabled,
    getUserPONotificationPreferences: state => state.userPONotificationPreferences

  },

  mutations: {

    /** Sets the user preferences to state. */
    SET_USER_PREFERENCES(state,userPreferences) {

      state.isLoaded = true;

      state.userPreferences.emailNotifications = !!userPreferences.emailNotifications;
      state.userPreferences.emailNotificationsReason = userPreferences.emailNotificationsReason;
      state.userPreferences.slackIntegration = !!userPreferences.slackIntegration;
      state.userPreferences.salesforceIntegration = !!userPreferences.salesforceIntegration;
      state.userPreferences.salesforceChatter = !!userPreferences.salesforceChatter;
      state.userPreferences.salesforceGroupId = userPreferences.salesforceGroupId;
      state.userPreferences.msTeamsIntegration = !!userPreferences.msTeamsIntegration;

      state.userNotificationPreferences.enabled =
        !!userPreferences.userNotificationPreferences.enabled;
      state.userNotificationPreferences.dailyOverdueTaskSummary =
        !!userPreferences.userNotificationPreferences.dailyOverdueTaskSummary;
      state.userNotificationPreferences.rejectionResolved =
        !!userPreferences.userNotificationPreferences.rejectionResolved;
      state.userNotificationPreferences.taskInProgress =
        !!userPreferences.userNotificationPreferences.taskInProgress;
      state.userNotificationPreferences.teamLeaderInvitation =
        !!userPreferences.userNotificationPreferences.teamLeaderInvitation;
      state.userNotificationPreferences.weeklyTaskSummary =
        !!userPreferences.userNotificationPreferences.weeklyTaskSummary;

      state.userPONotificationPreferences.enabled =
        !!userPreferences.userPONotificationPreferences.enabled;
      state.userPONotificationPreferences.fileRejected =
        !!userPreferences.userPONotificationPreferences.fileRejected;
      state.userPONotificationPreferences.projectCompleted =
        !!userPreferences.userPONotificationPreferences.projectCompleted;
      state.userPONotificationPreferences.projectOverdue =
        !!userPreferences.userPONotificationPreferences.projectOverdue;
      state.userPONotificationPreferences.projectOverdueSoon =
        !!userPreferences.userPONotificationPreferences.projectOverdueSoon;
      state.userPONotificationPreferences.statusChange =
        !!userPreferences.userPONotificationPreferences.statusChange;
      state.userPONotificationPreferences.unassignedFromOwnerRole =
        !!userPreferences.userPONotificationPreferences.unassignedFromOwnerRole;
      state.userPONotificationPreferences.taskInProgressPo =
        !!userPreferences.userPONotificationPreferences.taskInProgressPo;
    },

    /** Clears the user preferences from state. */
    RESET_USER_PREFERENCES(state) {

      state.isLoaded = false;

      state.userPreferences.emailNotifications = false;
      state.userPreferences.emailNotificationsReason = null;
      state.userPreferences.slackIntegration = false;
      state.userPreferences.salesforceIntegration = false;
      state.userPreferences.salesforceChatter = false;
      state.userPreferences.salesforceGroupId = null;
      state.userPreferences.msTeamsIntegration = false;

      state.userNotificationPreferences.enabled = false;
      state.userNotificationPreferences.dailyOverdueTaskSummary = false;
      state.userNotificationPreferences.rejectionResolved = false;
      state.userNotificationPreferences.taskInProgress = false;
      state.userNotificationPreferences.teamLeaderInvitation = false;
      state.userNotificationPreferences.weeklyTaskSummary = false;

      state.userPONotificationPreferences.enabled = false;
      state.userPONotificationPreferences.fileRejected = false;
      state.userPONotificationPreferences.projectCompleted = false;
      state.userPONotificationPreferences.projectOverdue = false;
      state.userPONotificationPreferences.projectOverdueSoon = false;
      state.userPONotificationPreferences.statusChange = false;
      state.userPONotificationPreferences.unassignedFromOwnerRole = false;
      state.userPONotificationPreferences.taskInProgressPo = false;
    }

  },

  actions: {

    /** Gets user preferences from back-end and sets it to state. */
    getUserPreferences: async function(context) {

      return await apiCall.get('/v1/preferences/get').then(result => {
        context.commit("SET_USER_PREFERENCES",result.data);

        logger.debug("Query getUserPreferences succeeded with result: ",result.data);

        return result.data;
      }).catch(error => {
        if(error.response && error.response.status !== 401) {
          // Unauthorized; Consider this a success with result "unauthorized"
          // Reject anyway, but make error object available for getting status
          logger.error("Query getUserPreferences failed with message: ",error);
        }
        throw error;
      });
    },

    /** Clears user preferences from local storage and state. */
    clearUserPreferences(context) {
      context.commit("RESET_USER_PREFERENCES");
    },

    /** Updates user notification preferences. */
    updateUserPreferences: async function(context,preferences) {

      let params = { ...context.state.userPreferences };

      // Note: Property 'emailNotificationsReason' is updated in the back-end.
      delete params['emailNotificationsReason'];

      for(let key in params) {
        if(params.hasOwnProperty(key) && preferences[key] != null)
          params[key] = preferences[key];
      }

      return await apiCall.put('/v1/preferences/update',params).then(result => {
        context.commit("SET_USER_PREFERENCES",result.data);

        logger.debug("Query updateUserPreferences succeeded with result: ",result.data);

        return result.data;
      }).catch(error => {
        logger.error("Query updateUserPreferences failed with message: ",error);

        throw error;
      });
    },

    /** Updates general user notification preferences. */
    updateUserNotificationPreferences: async function(context,preferences) {

      let params = { ...context.state.userNotificationPreferences };

      for(let key in params) {
        if(params.hasOwnProperty(key) && preferences[key] != null)
          params[key] = preferences[key];
      }

      return await apiCall.put('/v1/preferences/update-user-notifications',params).then(result => {
        context.commit("SET_USER_PREFERENCES",result.data);

        logger.debug("Query updateUserNotificationPreferences succeeded with result: ",result.data);

        return result.data;
      }).catch(error => {
        logger.error("Query updateUserNotificationPreferences failed with message: ",error);

        throw error;
      });
    },

    /** Updates project owner notification preferences. */
    updateUserPONotificationPreferences: async function(context,preferences) {

      let params = { ...context.state.userPONotificationPreferences };

      for(let key in params) {
        if(params.hasOwnProperty(key) && preferences[key] != null)
          params[key] = preferences[key];
      }

      return await apiCall.put('/v1/preferences/update-po-notifications',params).then(result => {
        context.commit("SET_USER_PREFERENCES",result.data);

        logger.debug("Query updateUserPONotificationPreferences succeeded with result: ",
                     result.data);

        return result.data;
      }).catch(error => {
        logger.error("Query updateUserPONotificationPreferences failed with message: ",error);

        throw error;
      });
    }
  }
};
